// Firestore-compatible useConfigurationData hook
// COMPLETE DROP-IN REPLACEMENT for express useConfigurationData hook
// Maintains exact same interface while using real-time Firestore listeners
// FULLY DRY: Imports ALL shared utilities, constants, and logic from express app

import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore'
import { db } from '../config/firebase.js'
import { useCloudContext } from '../contexts/CloudProvider.jsx'
// Import ALL shared constants and utilities from express app - FULLY DRY, no duplication
import { 
  DEFAULT_CONFIG,
  hasChanges,
  preprocessConfigDataForSave,
  processConfigurationData,
} from '@express/hooks/useConfigurationData.js'

const useConfigurationData = () => {
  const [isDirty, setIsDirty] = useState(false)
  const [lastSavedData, setLastSavedData] = useState(null)
  const [formData, setFormData] = useState(DEFAULT_CONFIG)
  
  const { selectedDeviceId } = useCloudContext()

  // Real-time Firestore listener using useState for immediate updates
  const [configData, setConfigData] = useState(null)
  const [firestoreLoading, setFirestoreLoading] = useState(true)
  const [firestoreError, setFirestoreError] = useState(null)

  // Set up real-time listener for configuration changes
  useEffect(() => {
    if (!selectedDeviceId) {
      setConfigData(null)
      setFirestoreLoading(false)
      setFirestoreError(null)
      return
    }

    setFirestoreLoading(true)
    setFirestoreError(null)

    const deviceConfigRef = doc(db, 'deviceConfigs', selectedDeviceId)
    
    const unsubscribe = onSnapshot(deviceConfigRef, (docSnap) => {
      try {
        if (docSnap.exists()) {
          const data = docSnap.data()
          const currentConfig = data.currentConfig || {}
          setConfigData(currentConfig)
        } else {
          setConfigData({})
        }
        setFirestoreLoading(false)
        setFirestoreError(null)
      } catch (dataError) {
        console.error('❌ Error processing Firestore data:', dataError)
        setFirestoreError(dataError)
        setFirestoreLoading(false)
      }
    }, (error) => {
      console.error('❌ Firestore listener error:', error)
      setFirestoreError(new Error('Failed to load configuration'))
      setFirestoreLoading(false)
    })

    return () => unsubscribe()
  }, [selectedDeviceId])

  // Use the real-time data for compatibility with existing code
  const isLoading = firestoreLoading
  const error = firestoreError

  // Memoize processed configuration data (SAME as express)
  const processedConfigData = useMemo(() => {
    if (!configData || Object.keys(configData).length === 0) {
      return null
    }
    
    try {
      const processed = processConfigurationData(configData)
      return processed
    } catch (processError) {
      console.error('❌ Error processing configuration data:', processError)
      return null
    }
  }, [configData])

  // REPLACE fetch() POST with Firestore setDoc wrapped in React Query mutation
  const saveConfigMutation = useMutation({
    mutationFn: async (data) => {
      if (!selectedDeviceId) {
        throw new Error('No device selected')
      }

      // Use shared data preprocessing from express app
      const processedData = preprocessConfigDataForSave(data)

      // CRITICAL: Preserve networkType from existing configuration
      // The WiFi/Ethernet section is hidden in cloud app, but networkType is essential for device function
      if (configData && configData.networkType && !processedData.networkType) {
        console.warn('Preserving networkType from existing configuration:', configData.networkType)
        processedData.networkType = configData.networkType
      }

      const deviceConfigRef = doc(db, 'deviceConfigs', selectedDeviceId)
      
      // Get current sequence number
      const currentSnap = await getDoc(deviceConfigRef)
      const currentData = currentSnap.exists() ? currentSnap.data() : null
      
      const newConfigData = {
        currentConfig: processedData,
        lastUpdated: serverTimestamp(),
        sequenceNumber: currentData ? (currentData.sequenceNumber || 0) + 1 : 1,
        lastUpdateSource: 'cloud',
      }
      
      await setDoc(deviceConfigRef, newConfigData)
      
      return processedData // Return saved data
    },
    onSuccess: (data) => {
      // Real-time listener will automatically update configData, 
      // so we don't need to manually invalidate queries
      // Just update local state for immediate feedback
      setLastSavedData(data)
      setIsDirty(false)
    },
    onError: (error) => {
      console.error('❌ Configuration save failed:', error)
    },
  })

  // EXACT SAME state management as express
  useEffect(() => {
    if (isLoading) return // Don't update while loading

    const hasConfigData = configData && Object.keys(configData).length > 0
    
    if (hasConfigData && processedConfigData) {
      startTransition(() => {
        setFormData(processedConfigData)
        setLastSavedData(processedConfigData)
        setIsDirty(false)
      })
    } else if (!isLoading) {
      // Only set default config if we're not loading and there's no data
      startTransition(() => {
        setFormData(DEFAULT_CONFIG)
        setLastSavedData(null)
        setIsDirty(false)
      })
    }
  }, [configData, processedConfigData, isLoading])

  // EXACT SAME dirty detection as express
  const markDirty = useCallback((newData) => {
    if (hasChanges(newData, lastSavedData)) {
      setIsDirty(true)
    } else {
      setIsDirty(false)
    }
  }, [lastSavedData])

  const saveConfiguration = useCallback(async (data) => {
    markDirty(data)
    return saveConfigMutation.mutateAsync(data)
  }, [saveConfigMutation, markDirty])

  // EXACT SAME INTERFACE as express hook
  const returnData = {
    formData,
    isLoading,
    error,
    saveConfiguration,
    isSaving: saveConfigMutation.isPending,
    isDirty,
    lastSavedData,
  }
  
  return returnData
}

export default useConfigurationData
